import { Task } from '@lit/task';
// @ts-expect-error moduleResolution:nodenext issue 54523
import { html, LitElement, TemplateResult, nothing } from 'lit';
// @ts-expect-error moduleResolution:nodenext issue 54523
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import LightElement from './light-element.js';
import { VariantSelectorElement } from "../../ui/components/product-variant-selector";
import { ISamples } from '../models/ISamples';
import { ISample } from '../models/ISample.js';
import Ecommerce from '../../typescript/modules/ecommerce.js';
import { IViewItemGA4Item } from "../../ui/models/IViewItemGA4Item";
import './toast-notification.ts';
import Tracking from '../../typescript/modules/tracking.js';

declare var dataLayer: any;

@customElement('product-sample')
export default class ProductSample extends LightElement {

	@property({ type: String })
	id: string;

	@state()
	isAdding = false;

	@state()
	errorMessage = "";

	private prefixLabel = 'Want to see the finish up close?';
	private viewedSampleIds = new Set<string>();
	private sampleViewObserver?: IntersectionObserver;

	private _sampleTask = new Task(this, {
		task: async ([id], { signal }) => {
			if (!id) return undefined;
			this.isAdding = false;
			this.errorMessage = "";
			const response = await fetch(`/api/sample/${id}`, { signal });
			if (!response.ok) { throw new Error(response.statusText); }
			const responseData = await response.json() as ISamples;
			return responseData;
		},
		args: () => [this.id]
	});

	protected updated() {
		this.setupSampleViewTracking();
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		this.teardownSampleViewObserver();
	}

	handleClick(event: Event) {
		event.preventDefault();

		const form = this.closest('form');
		if (!form) {
			return;
		}
		const variantSelector = form.querySelector('product-variant-selector') as VariantSelectorElement;
		if (variantSelector) {
			if (!variantSelector.isValid()) {
				return false;
			}
		}
		this.isAdding = true;
		this.addSampleToBasket(this._sampleTask.value);
	}

	addSampleToBasket(samples?: ISamples) {
		this.isAdding = true;

		return fetch(`/api/basketadd/addsamples`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ sampleIds: samples?.Samples.map(sample => sample.Name) })
		})
			.then((response) => {
				if (response.ok) {
					return response.json();
				} else {
					throw new Error(response.statusText);
				}
			})
			.then((response: any) => {
				this.isAdding = false;
				if (this._sampleTask.value) {
					this._sampleTask.value.IsAlreadyInBasket = true;
				}
				Ecommerce.updateBasketCounts(response.BasketTotalItemQuantity);
				this.trackAddToBasket(samples);
				this.addSuccessMessage(samples);

			})
			.catch(() => {
				this.isAdding = false;
				this.errorMessage = "Sorry, there was a problem adding the sample to your basket. Please try again later.";
			});
	}

	addSuccessMessage(samples: ISamples) {
		let toast = document.querySelector('toast-notification');
		if (!toast) {
			toast = document.createElement('toast-notification');
			document.body.appendChild(toast);
		}
		// @ts-ignore
		toast.show(
			'Added to your basket',
			`${samples.Samples[0].ShortDisplayName}`,
			'success',
			7000,
			'bottom',
			'/basket'
		);
	}

	getProductText(samples?: ISamples): TemplateResult {
		if (!this.id || !this.id) return html`<span class="c-product-sample__inline-title">${this.prefixLabel}</span> Order a sample`;
		if (!samples?.IsInStock) return html`No sample for this finish - explore similar finishes or chat with our team`;
		return html`<span class="c-product-sample__inline-title">${this.prefixLabel}</span> Order a <span class="u-bold">${samples.Samples[0]?.ShortDisplayName}</span>${samples.Samples[0]?.Price ? html` for <span class="u-bold">${unsafeHTML(samples.Samples[0].Price.Formatted)}</span>` : nothing}`;
	}

	getButtonText(samples?: ISamples): TemplateResult {
		if (!this.id || !this.id) return html`Select an option`;
		if (this.isAdding) return html`Adding...`;
		if (samples?.IsAlreadyInBasket) return html`In basket`;
		return html`Add<span class="c-product-sample__button-suffix"> sample</span> <span class="u-visuallyhidden"> to basket</span>`;
	}

	trackAddToBasket(samples?: ISamples) {

		let value: number = 0;
		let gaData: IViewItemGA4Item[] = [];

		for (let i = 0; i < samples.Samples.length; i++) {
			const currentSample = samples.Samples[i];
			let viewItemEventInfo = JSON.parse(currentSample.ViewItemEventInfoJson) as IViewItemGA4Item;
			viewItemEventInfo.quantity = 1;
			value += currentSample.Price.Price * 1;
			gaData.push(viewItemEventInfo);
		}

		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: 'add_to_cart',
			ecommerce: {
				value: value,
				items: gaData
			}
		});
	}

	private setupSampleViewTracking() {
		const sampleResponse = this._sampleTask.value;
		if (!sampleResponse?.HasSamples || !sampleResponse.IsInStock || !sampleResponse.Samples?.length) {
			this.teardownSampleViewObserver();
			return;
		}

		const sample = sampleResponse.Samples[0];
		if (!sample?.Name || this.viewedSampleIds.has(sample.Name)) {
			this.teardownSampleViewObserver();
			return;
		}

		const sampleElement = this.renderRoot.querySelector('.c-product-sample') as HTMLElement;
		if (!sampleElement) {
			return;
		}

		this.teardownSampleViewObserver();

		if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
			if (!this.viewedSampleIds.has(sample.Name)) {
				Tracking.trackViewItem(sample.Price.Price, [JSON.parse(sample.ViewItemEventInfoJson)]);
				this.viewedSampleIds.add(sample.Name);
			}
			return;
		}

		this.sampleViewObserver = new IntersectionObserver((entries) => {
			const entry = entries[0];
			if (!entry?.isIntersecting) {
				return;
			}

			if (!this.viewedSampleIds.has(sample.Name)) {
				Tracking.trackViewItem(sample.Price.Price, [JSON.parse(sample.ViewItemEventInfoJson)]);
				this.viewedSampleIds.add(sample.Name);
			}

			this.teardownSampleViewObserver();
		}, { threshold: 0.1 });

		this.sampleViewObserver.observe(sampleElement);
	}

	private teardownSampleViewObserver() {
		if (this.sampleViewObserver) {
			this.sampleViewObserver.disconnect();
			this.sampleViewObserver = undefined;
		}
	}

	private placeholderIcon(): TemplateResult {
		return html`
		<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 45 45" class="c-product-sample__button-icon">
			<g>
				<rect width="45" height="45" style="fill:none; stroke-width:0px;"/>
				<path d="m15.75,12.37l-3.38,3.38" style="fill:none; stroke:currentColor; stroke-linecap:round; stroke-miterlimit:10; stroke-width:1.84px;"/>
				<path d="m32.63,29.25l-3.38,3.38" style="fill:none; stroke:currentColor; stroke-linecap:round; stroke-miterlimit:10; stroke-width:1.84px;"/>
				<path d="m21.37,12.37l-9,9" style="fill:none; stroke:currentColor; stroke-linecap:round; stroke-miterlimit:10; stroke-width:1.84px;"/>
				<path d="m27,12.37l-14.63,14.63" style="fill:none; stroke:currentColor; stroke-linecap:round; stroke-miterlimit:10; stroke-width:1.84px;"/>
				<path d="m32.63,12.37l-20.25,20.25" style="fill:none; stroke:currentColor; stroke-linecap:round; stroke-miterlimit:10; stroke-width:1.84px;"/>
				<path d="m32.63,18l-14.63,14.63" style="fill:none; stroke:currentColor; stroke-linecap:round; stroke-miterlimit:10; stroke-width:1.84px;"/>
				<path d="m32.63,23.63l-9,9" style="fill:none; stroke:currentColor; stroke-linecap:round; stroke-miterlimit:10; stroke-width:1.84px;"/>
				<rect x="5" y="5" width="35" height="35" rx="2.62" ry="2.62" style="fill:none; stroke:currentColor; stroke-miterlimit:10; stroke-width:1.23px;"/>
			</g>
		</svg>
		`;
	}

	render() {

		if(this.errorMessage) {
			return html`
				<div class="c-product-sample">
					<div class="c-product-sample__body">
						<div class="c-product-sample__details">
							${this.placeholderIcon()}
							<span class="c-product-sample__text">${this.errorMessage}</span>
						</div>
					</div>
				</div>
			`;
		}

		if (!this.id) {
			return html`
				<div class="c-product-sample">
					<span class="c-product-sample__label u-bold">${this.prefixLabel}</span>
					<div class="c-product-sample__body">
						<div class="c-product-sample__details">
							${this.placeholderIcon()}
							<span class="c-product-sample__text">
								<span class="c-product-sample__inline-title">${this.prefixLabel}</span> Order a sample
							</span>
						</div>
						<button class="c-product-sample__button c-button c-button--sample" data-test="product-sample-button" type="button" @click="${(event) => this.handleClick(event)}">
							Select an option
						</button>
					</div>
				</div>
			`;
		}

		return this._sampleTask.render({
			pending: () => html`
				<div class="c-product-sample">
					<div class="c-product-sample__body">
						<div class="c-product-sample__details">
							${this.placeholderIcon()}
							<span class="c-product-sample__text">Loading sample...</span>
						</div>
					</div>
				</div>
			`,
			complete: (sampleResponse) => {
				return !sampleResponse?.HasSamples ? html`
				<div class="c-product-sample">
					<div class="c-product-sample__body">
						<div class="c-product-sample__details">
							${this.placeholderIcon()}
							<span class="c-product-sample__text">No sample for this finish - explore similar finishes or chat with our team</span>
						</div>
					</div>
				</div>
				`	:
				html`
				<div class="c-product-sample">
					<span class="c-product-sample__label u-bold">${this.prefixLabel}</span>
						<div class="c-product-sample__body">
							<div class="c-product-sample__details">
								${!sampleResponse?.Samples[0].ImageUrl || !sampleResponse.IsInStock ?
								html`${this.placeholderIcon()}`
								: html`<img src=${sampleResponse?.Samples[0]?.ImageUrl} alt=${sampleResponse?.Samples[0]?.ImageAlt} width="36" height="36" class="c-product-sample__button-icon c-product-sample__button-icon--swatch">`}
								<span class="c-product-sample__text">
									${this.getProductText(sampleResponse)}
								</span>
							</div>
							${sampleResponse.IsInStock ? 
								html`<button class="c-product-sample__button c-button c-button--sample" data-test="product-sample-button" type="button" ?disabled="${(!!this.id && !!this.id) && (!sampleResponse.IsInStock || sampleResponse.IsAlreadyInBasket || this.isAdding)}" @click="${(event) => this.handleClick(event)}">
									${this.getButtonText(sampleResponse)}
								</button>`
							: nothing}
					</div>
				</div>
			`},
			error: (e) => html`
				<div class="c-product-sample">
					<div class="c-product-sample__body">
						<div class="c-product-sample__details">
							${this.placeholderIcon()}
							<span class="c-product-sample__text">An error occurred loading the sample...</span>
						</div>
					</div>
				</div>
			`
		});
	}

}
